import React, { useEffect, useRef } from 'react';
import './Panel.css';

// The shell every slide-in panel shares: Requests, Messages, Profile.
//
// 2026-10-15: there were five drawers, each with its own width, offset, radius, surface and
// transition, all opening over the same map. This is the one shell, so they can differ in what they
// contain and in nothing else.
//
// It stays MOUNTED while closed (translated off-screen) rather than unmounting, so the slide-out is
// visible — a panel that vanishes the instant it is dismissed reads as a glitch rather than a
// movement. `inert`-like behaviour is handled by aria-hidden plus the focus trap below.
const Panel = ({ open, title, count, onClose, onBack, children, labelledBy, bodyClass, onTitleClick }) => {
  const ref = useRef(null);

  // The panel is `fixed`, so the document cannot lay it out inside the map — but that is exactly
  // where it belongs: starting below the header (covering it would hide the controls that opened
  // this) and ending where the MAP ends, not at the bottom of the window. 2026-10-18: it ran past
  // the map's lower edge and over the page beneath it, which looked like a panel that had
  // overshot.
  //
  // So the map's own box is measured and handed to the CSS as three custom properties — it is
  // inset to the MAP, not to the window, on every side it touches. Measured rather than hardcoded
  // because all of those edges genuinely move: .App-header is a flex-wrap row inside
  // a container query, and the strip below the map changes with the window.
  useEffect(() => {
    const target = document.querySelector('.map-container') || document.querySelector('.App-header');
    if (!target) return undefined;
    const apply = () => {
      const r = target.getBoundingClientRect();
      const root = document.documentElement.style;
      // .App-header fallback: there is no map, so take the space under the header.
      const top = target.classList.contains('map-container') ? r.top : r.bottom;
      root.setProperty('--panel-top', `${Math.max(0, Math.round(top))}px`);
      root.setProperty('--panel-bottom', `${Math.max(0, Math.round(window.innerHeight - r.bottom))}px`);
      // 2026-10-18: and the right edge too, which was flush to the WINDOW while the map sits 5px
      // inside it (.map-container has a margin and a border). The panel overhung the map by those
      // few pixels and read as attached to the browser rather than to the app.
      root.setProperty('--panel-right', `${Math.max(0, Math.round(window.innerWidth - r.right))}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(target);
    // ResizeObserver sees the element's own box change; it does not see the window get shorter
    // underneath it, which moves the bottom inset without resizing anything.
    window.addEventListener('resize', apply);
    window.addEventListener('scroll', apply, { passive: true });
    return () => { ro.disconnect(); window.removeEventListener('resize', apply); window.removeEventListener('scroll', apply); };
  }, []);

  // Escape closes it. Bound to the document rather than the panel because the panel will not have
  // focus when it opens from a header button, and the key has to work regardless.
  //
  // Unless a dialog is open ON TOP of it — Car details, Update email/password, Map & filters, the
  // log-out confirmation. The listener is on the document, so without this the panel underneath
  // would take the Escape meant for the dialog and close itself, leaving the dialog floating over
  // a map with nothing behind it. (`avatar-edit-overlay` is the hover label on the profile picture
  // and is always present, hence matching the end of the class rather than any part of it.)
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('.confirm-overlay, .modal-overlay, [class$="modal-overlay"]')) return;
      if (onBack) onBack(); else onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, onBack]);

  return (
    <aside
      ref={ref}
      className={`panel ${open ? 'open' : ''}`}
      aria-hidden={!open}
      aria-labelledby={labelledBy}
      role="complementary"
    >
      <div className="panel-head">
        {/* A panel that drills in needs a way back out that is not the close button: Messages opens
            on a list and goes one level deeper into a conversation, and closing from there loses
            the list too. */}
        {onBack && (
          <button type="button" className="panel-back" onClick={onBack} aria-label="Back">
            <i className="fas fa-arrow-left" />
          </button>
        )}
        {/* onTitleClick: a drilled-in panel whose title names a PERSON can open that person's
            card from it — the header of a conversation, which is where every messaging app puts
            it. A heading that is also a control is still a heading, so the <h2> stays and the
            button lives inside it. */}
        <h2 className="panel-title" id={labelledBy}>
          {onTitleClick ? (
            <button type="button" className="panel-title-button" onClick={onTitleClick}>
              {title}
            </button>
          ) : title}
        </h2>
        {count > 0 && <span className="panel-count">{count}</span>}
        {/* 2026-10-18: the × is gone. Each panel is toggled by the header button directly above it,
            which stays visible the whole time the panel is open and now shows a pressed state — so
            the close control was a second one for the same job, in a header bar that already had
            one. Escape still closes, and a drilled-in view still gets its back arrow above. */}
      </div>
      {/* bodyClass: a panel whose content pins something to its foot — the messages composer —
          needs the body to be a flex column that does NOT scroll as one block, with the scrolling
          happening inside it instead. Everything else keeps the plain scrolling body. */}
      <div className={`panel-body${bodyClass ? ` ${bodyClass}` : ''}`}>{children}</div>
    </aside>
  );
};

export default Panel;
